import type {
	FluentDateTime,
	FluentFunction,
	FluentNumber,
} from '@fluent/bundle'

import {
	formatRelativeDate,
	formatRelativeDateTime,
	formatRelativeTime,
	formatTimeDuration,
	getDateIntervalDuration,
} from '@/utilities/dates.ts'
import { formatFileSize } from '@/utilities/fileSize.ts'
import { capitalize } from '@/utilities/strings.ts'

export default function getFunctions(
	locale: string,
): Record<string, FluentFunction> {
	return {
		CAPITALIZE(positional) {
			const [value] = positional as [string]

			return capitalize(value, locale)
		},

		DISPLAY_NAME(positional, named) {
			const [code] = positional as [string]
			const { type } = named as { type: Intl.DisplayNamesType }
			const formatter = new Intl.DisplayNames(locale, { type })

			return formatter.of(code) ?? code
		},

		LIST(positional, named) {
			const items = positional as string[]
			const { type, style } = named as Intl.ListFormatOptions
			const formatter = new Intl.ListFormat(locale, { type, style })

			return formatter.format(items.filter((item) => item))
		},

		RELATIVE_TIME(positional) {
			const [date] = positional as [FluentDateTime]

			return formatRelativeTime(locale, new Date(normalizeFluentDateTime(date)))
		},

		RELATIVE_DATE(positional) {
			const [date] = positional as [FluentDateTime]

			return formatRelativeDate(locale, new Date(normalizeFluentDateTime(date)))
		},

		RELATIVE_DATE_TIME(positional) {
			const [date] = positional as [FluentDateTime]

			return formatRelativeDateTime(
				locale,
				new Date(normalizeFluentDateTime(date)),
			)
		},

		TIME_DURATION(positional) {
			const [start, end] = positional as [
				FluentDateTime | FluentNumber,
				FluentDateTime | FluentNumber,
			]

			const duration = getDateIntervalDuration(
				normalizeFluentDateTime(start),
				normalizeFluentDateTime(end),
			)
			return formatTimeDuration(duration)
		},

		FILE_SIZE(positional) {
			const [size] = positional as [FluentNumber]

			return formatFileSize(locale, size.value)
		},
	}
}

/**
 * Turn a FluentDateTime object or a FluentNumber into a JS Date or number type.
 */
function normalizeFluentDateTime({
	value,
}: FluentDateTime | FluentNumber): Date | number {
	if (value instanceof Date || typeof value === 'number') {
		return value
	}

	/** Note: Temporal isn't supported in browsers yet. This satifies typing. */
	return value.epochMilliseconds ?? 0
}
